import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { getApiUrl } from '../lib/api';

export const Route = createFileRoute('/activate')({
  component: ActivatePage,
});

function ActivatePage() {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  
  const { signIn, checkSession } = useAuthStore();
  const navigate = useNavigate();

  // Define handleActivate with useCallback so it can be used in useEffect
  const handleActivate = useCallback(async (activationCode: string, sessionToken: string, refreshToken: string) => {
    try {
      setIsActivating(true);
      setError(null);

      const response = await fetch(getApiUrl('DEVICE_ACTIVATE'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({
          code: activationCode,
          sessionToken,
          refreshToken,
        }),
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          // If response is not JSON, get text
          const text = await response.text();
          throw new Error(text || `HTTP ${response.status}: ${response.statusText}`);
        }
        throw new Error(errorData.message || errorData.error || 'Failed to activate device');
      }

      setSuccess(true);
      
      // Refresh session to ensure everything is up to date
      await checkSession();
      
      // Redirect to home after a short delay
      setTimeout(() => {
        navigate({ to: '/' });
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to activate device');
      setIsActivating(false);
    }
  }, [checkSession, navigate]);

  // Get code from URL params if present (runs first)
  useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
    const urlCode = urlParams.get('code');
    if (urlCode) {
      setCode(urlCode);
        }
  }, []);

  // Handle OAuth callback and activation
  useEffect(() => {
    const handleOAuthCallback = async () => {
      console.log('🔄 OAuth callback detected, checking session...');
      
      // Extract code from URL params (in case state hasn't updated yet)
      const urlParams = new URLSearchParams(window.location.search);
      const activationCode = urlParams.get('code') || code;
      
      if (!activationCode) {
        console.error('❌ No activation code found in URL or state');
        setError('Activation code not found. Please enter the code again.');
        setIsSigningIn(false);
        return;
      }

      // Wait a bit for Supabase to process the OAuth callback
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('❌ Session error:', error);
        setError(error.message);
        setIsSigningIn(false);
        return;
      }

      if (!session) {
        console.error('❌ No session found after OAuth');
        setError('Failed to create session. Please try again.');
        setIsSigningIn(false);
        return;
      }

      console.log('✅ Session found, activating device with code:', activationCode);
      setIsSigningIn(false);
      await handleActivate(activationCode, session.access_token, session.refresh_token);
    };

    // Check if this is an OAuth callback (hash contains access_token)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const hasAccessToken = hashParams.get('access_token');
    
    if (hasAccessToken) {
      console.log('🔐 OAuth callback detected in hash');
      handleOAuthCallback();
    } else {
      // Also check if user is already authenticated and we have a code
      const checkExistingAuth = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const activationCode = urlParams.get('code') || code;
        
        if (activationCode) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session && !isActivating && !success) {
            console.log('✅ User already authenticated, activating with code:', activationCode);
            await handleActivate(activationCode, session.access_token, session.refresh_token);
          }
        }
      };
      
      // Small delay to ensure code state is set
      const timer = setTimeout(() => {
        checkExistingAuth();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [code, isActivating, success, handleActivate]);

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError(null);
  };

  const handleSignIn = async () => {
    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setIsSigningIn(true);
      setError(null);
      
      // Check if user is already signed in
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        // Already signed in, proceed with activation
        await handleActivate(code, session.access_token, session.refresh_token);
      } else {
        // Need to sign in first
        const { error: signInError } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: `${window.location.origin}/activate?code=${code}`,
            queryParams: {
              access_type: 'offline',
              prompt: 'consent',
            },
          },
        });

        if (signInError) {
          throw signInError;
        }
        // User will be redirected to Google, then back here
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
      setIsSigningIn(false);
    }
  };


  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-[#141414] rounded-lg border border-gray-800 p-8 shadow-2xl"
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Activate Your TV</h1>
          <p className="text-gray-400">
            Enter the code shown on your TV to sign in
          </p>
        </div>

        {success ? (
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
            </motion.div>
            <h2 className="text-2xl font-bold text-white mb-2">Device Activated!</h2>
            <p className="text-gray-400 mb-4">Your TV is now signed in.</p>
            <p className="text-sm text-gray-500">Redirecting to home...</p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <label htmlFor="code" className="block text-sm font-medium text-gray-300 mb-2">
                Activation Code
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={handleCodeChange}
                placeholder="000000"
                className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white text-center text-3xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                disabled={isSigningIn || isActivating}
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Enter the 6-digit code from your TV
              </p>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg flex items-start gap-2"
              >
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-400 text-sm">{error}</p>
              </motion.div>
            )}

            <button
              onClick={handleSignIn}
              disabled={code.length !== 6 || isSigningIn || isActivating}
              className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
              {isSigningIn ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in with Google...
                </>
              ) : isActivating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Activating device...
                </>
              ) : (
                'Sign in with Google'
              )}
            </button>

            <p className="text-xs text-gray-500 mt-4 text-center">
              You'll be redirected to Google to sign in, then your TV will be activated automatically.
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}

